import { makeObservable, observable } from "mobx";
import type { UserSettingsResponse } from "../models";
import apiService from "./apiService";
import { PromiseCompletion } from "../classes/PromiseCompletion";

class UserService {
  @observable theme = localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
  @observable systemPrompt = localStorage.getItem('defaultSystemPrompt') || '';
  @observable importantInfo = localStorage.getItem('importantInfo') || '';
  @observable showTokenUsage = localStorage.getItem('showTokenUsage') === 'true';
  @observable showFollowUps = localStorage.getItem('showFollowUps') !== 'false';
  @observable showArchive = localStorage.getItem('showArchive') === 'true';
  
  @observable defaultShowFullThinking = localStorage.getItem('defaultShowFullThinking') === 'true';
  @observable defaultModelName = localStorage.getItem('defaultModelName') || 'gpt-oss:20b';
  @observable defaultTools: string[] | null = !!localStorage.getItem('defaultTools') ? JSON.parse(localStorage.getItem('defaultTools') || '[]') : null;
  @observable defaultTemperature: number = parseFloat(localStorage.getItem('defaultTemperature') || '0.8');
  @observable defaultContextLength: string | null = localStorage.getItem('defaultContextLength') || 'auto';
  @observable defaultWhisperLanguage: string = localStorage.getItem('defaultWhisperLanguage') || 'auto';
  @observable defaultEnableSyntaxHighlighting: boolean = localStorage.getItem('defaultEnableSyntaxHighlighting')?.toLowerCase() !== 'false';
  @observable defaultThinkMode: string = localStorage.getItem('defaultThinkMode')?.toLowerCase() || 'true';

  public settingsLoader: PromiseCompletion = new PromiseCompletion();
  
  constructor() {
    makeObservable(this);
  }

  public async loadUserSettings(): Promise<void> {
    const data = await apiService.get<UserSettingsResponse>('/api/user/settings', { loader: this.settingsLoader });
    
    this.theme = data.theme || (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    this.systemPrompt = data.defaultSystemPrompt;
    this.showTokenUsage = data.showTokenUsage ?? false;
    this.showFollowUps = data.showFollowUps ?? true;
    this.showArchive = data.showArchive ?? false;
    this.defaultTools = data.defaultTools ?? null;
    this.defaultModelName = data.defaultModelName || 'gpt-oss:20b';
    this.defaultShowFullThinking = data.defaultShowFullThinking ?? false;
    this.defaultTemperature = data.defaultTemperature !== undefined ? data.defaultTemperature : 0.8;
    this.defaultContextLength = data.defaultContextLength ?? null;
    this.defaultWhisperLanguage = data.defaultWhisperLanguage || 'auto';
    this.defaultEnableSyntaxHighlighting = data.defaultEnableSyntaxHighlighting ?? true;
    this.defaultThinkMode = data.defaultThinkMode ?? 'true';
    this.importantInfo = data.importantInfo || '';

    localStorage.setItem('theme', this.theme);
    document.documentElement.setAttribute('data-theme', this.theme);
    localStorage.setItem('defaultSystemPrompt', this.systemPrompt || '');
    localStorage.setItem('importantInfo', this.importantInfo || '');
    localStorage.setItem('showTokenUsage', this.showTokenUsage ? 'true' : 'false');
    localStorage.setItem('showFollowUps', this.showFollowUps ? 'true' : 'false');
    localStorage.setItem('showArchive', this.showArchive ? 'true' : 'false');
    localStorage.setItem('defaultModelName', this.defaultModelName || 'gpt-oss:20b');
    localStorage.setItem('defaultTools', this.defaultTools ? JSON.stringify(this.defaultTools) : '');
    localStorage.setItem('defaultShowFullThinking', this.defaultShowFullThinking ? 'true' : 'false');
    localStorage.setItem('defaultTemperature', this.defaultTemperature.toString());
    localStorage.setItem('defaultContextLength', this.defaultContextLength === null ? 'null' : this.defaultContextLength.toString());
    localStorage.setItem('defaultWhisperLanguage', this.defaultWhisperLanguage);
    localStorage.setItem('defaultEnableSyntaxHighlighting', this.defaultEnableSyntaxHighlighting ? 'true' : 'false');
    localStorage.setItem('defaultThinkMode', this.defaultThinkMode ? 'true' : 'false');
  }
}

export const userService = new UserService();